import { Stack, Typography } from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Page from '../../../components/Page';
import Article from '../../../components/Article';
import CustomButton from '../../../components/CustomButton';
import ThemedTextField from '../../../components/ThemedTextField';
import { useTenant } from '../../../contexts/TenantContext';
import { Routes } from '../../../routes/RouteConstants';
import AuthLink from '../components/AuthLink';
import MarketingConsentCheckbox from '../components/MarketingConsentCheckbox';
import PendingVerificationNotice from '../components/PendingVerificationNotice';
import useAuthRequest from '../useAuthRequest';
import useAuthNextStep from '../useAuthNextStep';
import { emailRule, nameRule, newPasswordRule, phoneNumberRule } from '../validation';

const validationSchema = yup.object({
  name: nameRule,
  phoneNumber: phoneNumberRule,
  email: emailRule,
  password: newPasswordRule,
});

// All four fields at once (docs/EMAIL_AUTH.md A4), so a guest who registers here never sees
// /complete-registration. The answer is the same whether or not the address has an account (A7).
const RegisterLayout = () => {
  const { displayConfig } = useTenant();
  const register = useAuthRequest("register");
  const goToNextStep = useAuthNextStep();

  const formik = useFormik({
    initialValues: {
      name: '',
      phoneNumber: '',
      email: '',
      password: '',
      marketingConsent: false,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const authResponse = await register.send(values);
      goToNextStep(authResponse, { email: values.email.trim(), purpose: "REGISTRATION" });
    },
  });

  return (
    <Page>
      <Article xsHorizontalPadding={2} title="Regisztráció" titleVariant="h5">
        <PendingVerificationNotice />
        <form onSubmit={formik.handleSubmit} style={{ width: '100%' }} noValidate>
          <Stack spacing={3}>
            <ThemedTextField
              id="name"
              name="name"
              label="Név"
              autoComplete="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
            <ThemedTextField
              id="phoneNumber"
              name="phoneNumber"
              label="Telefonszám"
              type="tel"
              autoComplete="tel"
              placeholder="+36 30 123 4567"
              value={formik.values.phoneNumber}
              onChange={formik.handleChange}
              error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
              helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
            />
            <ThemedTextField
              id="email"
              name="email"
              label="Email cím"
              type="email"
              autoComplete="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
            <ThemedTextField
              id="password"
              name="password"
              label="Jelszó"
              type="password"
              autoComplete="new-password"
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={(formik.touched.password && formik.errors.password) || "Legalább 8 karakter"}
            />
            <MarketingConsentCheckbox
              checked={formik.values.marketingConsent}
              onChange={formik.handleChange}
            />
            <CustomButton text="Regisztráció" type="submit" isLoading={register.loading} />
          </Stack>
        </form>

        <Typography variant="body2" color={displayConfig?.color} mt={3} textAlign="center">
          Már van fiókod? <AuthLink to={Routes.LOGIN}>Bejelentkezés</AuthLink>
        </Typography>
      </Article>
    </Page>
  );
};

export default RegisterLayout;
